/**
 * „Meine Filter" im Phase Board (#141): gespeicherte Filter aufrufen, anlegen, umbenennen,
 * überschreiben, löschen. Reine Bedienung — die Regeln (Name, Höchstzahl, Bereinigen) stehen in
 * `lib/saved-filters.ts`, das Speichern in `lib/use-saved-filters.ts`, das Anwenden im Board.
 *
 * Begriff: „Gespeicherter Filter", nicht „Sicht" (die ist in der PV die Persona).
 */
import type { SavedBoardFilter } from '@projektverwaltung/shared';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogFehler } from '../../lib/saved-filters.js';
import { useConfirm } from './confirm-dialog.js';
import { Modal } from './modal.js';
import { Popover } from './popover.js';

type Props = {
  liste: readonly SavedBoardFilter[];
  /** Erst wenn die Präferenzen geladen sind, darf gespeichert werden. */
  bereit: boolean;
  /** Höchstzahl erreicht → „Filter speichern…" gesperrt, mit Hinweis. */
  voll: boolean;
  onAufrufen: (f: SavedBoardFilter) => void;
  /** `null` = gespeichert, sonst der Grund, warum nicht — der Dialog bleibt dann offen. */
  onAnlegen: (name: string) => Promise<DialogFehler | null>;
  onUmbenennen: (id: string, name: string) => Promise<DialogFehler | null>;
  onUeberschreiben: (id: string) => Promise<void>;
  onLoeschen: (id: string) => Promise<void>;
};

export function GespeicherteFilter(props: Props) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<
    { art: 'anlegen' } | { art: 'umbenennen'; filter: SavedBoardFilter } | null
  >(null);

  return (
    <fieldset
      className="flex items-center gap-2 flex-wrap border-0 p-0 m-0 min-w-0"
      aria-label={t('v2.board.saved_title', 'Meine Filter')}
    >
      <span
        className="text-[11px] uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
      >
        {t('v2.board.saved_title', 'Meine Filter')}
      </span>
      {props.liste.map((f) => (
        <Eintrag
          key={f.id}
          filter={f}
          onAufrufen={() => props.onAufrufen(f)}
          onUmbenennen={() => setDialog({ art: 'umbenennen', filter: f })}
          onUeberschreiben={() => props.onUeberschreiben(f.id)}
          onLoeschen={() => props.onLoeschen(f.id)}
        />
      ))}
      <button
        type="button"
        onClick={() => setDialog({ art: 'anlegen' })}
        disabled={!props.bereit || props.voll}
        title={
          props.voll
            ? t('v2.board.saved_err_zu_viele', 'Höchstens 20 gespeicherte Filter.')
            : undefined
        }
        className="text-[11px] px-2 py-1 rounded-md transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          color: 'var(--primary)',
          border: '1px dashed var(--line-soft)',
          fontFamily: 'var(--f-mono)',
        }}
      >
        + {t('v2.board.saved_save', 'Filter speichern…')}
      </button>
      {dialog && (
        <NamensDialog
          titel={
            dialog.art === 'anlegen'
              ? t('v2.board.saved_dialog_title', 'Aktuellen Filter speichern')
              : t('v2.board.saved_dialog_rename', 'Filter umbenennen')
          }
          start={dialog.art === 'umbenennen' ? dialog.filter.name : ''}
          onAbbrechen={() => setDialog(null)}
          onSpeichern={async (name) => {
            const fehler =
              dialog.art === 'anlegen'
                ? await props.onAnlegen(name)
                : await props.onUmbenennen(dialog.filter.id, name);
            if (!fehler) setDialog(null);
            return fehler;
          }}
        />
      )}
    </fieldset>
  );
}

function Eintrag(props: {
  filter: SavedBoardFilter;
  onAufrufen: () => void;
  onUmbenennen: () => void;
  onUeberschreiben: () => Promise<void>;
  onLoeschen: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const confirmAction = useConfirm();
  const anker = useRef<HTMLButtonElement>(null);
  const [menueOffen, setMenueOffen] = useState(false);
  const name = props.filter.name;

  const menuePunkt =
    'block w-full text-left text-sm px-3 py-1.5 rounded hover:bg-[var(--surface-2)]';
  return (
    <span
      className="inline-flex items-center rounded-md"
      style={{ border: '1px solid var(--line-soft)', background: 'var(--surface)' }}
    >
      <button
        type="button"
        onClick={props.onAufrufen}
        className="text-xs px-2 py-1 rounded-l-md hover:bg-[var(--surface-2)]"
        style={{ color: 'var(--ink)' }}
        title={t('v2.board.saved_apply_hint', 'Filter „{{name}}" anwenden', { name })}
      >
        {name}
      </button>
      <button
        ref={anker}
        type="button"
        onClick={() => setMenueOffen((v) => !v)}
        className="text-xs px-1.5 py-1 rounded-r-md hover:bg-[var(--surface-2)]"
        style={{ color: 'var(--ink-3)', borderLeft: '1px solid var(--line-soft)' }}
        aria-label={t('v2.board.saved_menu', 'Aktionen für „{{name}}"', { name })}
        aria-expanded={menueOffen}
      >
        ⋯
      </button>
      {menueOffen && (
        <Popover
          anker={anker}
          onClose={() => setMenueOffen(false)}
          label={t('v2.board.saved_menu', 'Aktionen für „{{name}}"', { name })}
          breite={260}
          hoehe={160}
        >
          <div className="p-1">
            <button
              type="button"
              className={menuePunkt}
              onClick={() => {
                setMenueOffen(false);
                props.onUmbenennen();
              }}
            >
              {t('v2.board.saved_rename', 'Umbenennen…')}
            </button>
            <button
              type="button"
              className={menuePunkt}
              onClick={async () => {
                setMenueOffen(false);
                const ja = await confirmAction({
                  title: t(
                    'v2.board.saved_overwrite_title',
                    '„{{name}}" mit den aktuell gesetzten Filtern überschreiben?',
                    { name },
                  ),
                  confirmLabel: t('v2.board.saved_overwrite_confirm', 'Überschreiben'),
                });
                if (ja) await props.onUeberschreiben();
              }}
            >
              {t('v2.board.saved_overwrite', 'Mit aktuellem Stand überschreiben')}
            </button>
            <button
              type="button"
              className={menuePunkt}
              style={{ color: 'var(--danger)' }}
              onClick={async () => {
                setMenueOffen(false);
                const ja = await confirmAction({
                  title: t(
                    'v2.board.saved_delete_title',
                    'Gespeicherten Filter „{{name}}" löschen?',
                    {
                      name,
                    },
                  ),
                  body: t(
                    'v2.board.saved_delete_body',
                    'Die gerade gesetzten Filter bleiben, nur der gespeicherte Eintrag verschwindet.',
                  ),
                  confirmLabel: t('v2.board.saved_delete_confirm', 'Löschen'),
                  tone: 'danger',
                });
                if (ja) await props.onLoeschen();
              }}
            >
              {t('v2.board.saved_delete', 'Löschen…')}
            </button>
          </div>
        </Popover>
      )}
    </span>
  );
}

function NamensDialog(props: {
  titel: string;
  start: string;
  onAbbrechen: () => void;
  onSpeichern: (name: string) => Promise<DialogFehler | null>;
}) {
  const { t } = useTranslation();
  const titelId = useId();
  const feldId = useId();
  const [name, setName] = useState(props.start);
  const [fehler, setFehler] = useState<DialogFehler | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const fehlerText: Record<DialogFehler, string> = {
    speichern_fehlgeschlagen: t(
      'v2.board.saved_err_speichern',
      'Speichern fehlgeschlagen — bitte erneut versuchen.',
    ),
    name_leer: t('v2.board.saved_err_name_leer', 'Bitte einen Namen eingeben.'),
    name_zu_lang: t('v2.board.saved_err_name_zu_lang', 'Höchstens 60 Zeichen.'),
    name_vergeben: t('v2.board.saved_err_name_vergeben', 'Diesen Namen gibt es schon.'),
    zu_viele: t('v2.board.saved_err_zu_viele', 'Höchstens 20 gespeicherte Filter.'),
  };

  const absenden = async () => {
    setLaeuft(true);
    try {
      setFehler(await props.onSpeichern(name));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    // Hält eine Eingabe → weder Hintergrundklick noch Escape schließen (CLAUDE.md, <Modal>).
    <Modal
      open
      onClose={props.onAbbrechen}
      labelledBy={titelId}
      closeOnBackdrop={false}
      closeOnEscape={false}
      panelClassName="w-full max-w-md overflow-hidden"
    >
      <form
        className="p-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void absenden();
        }}
      >
        <h2 id={titelId} className="text-base font-medium" style={{ color: 'var(--ink)' }}>
          {props.titel}
        </h2>
        <label htmlFor={feldId} className="block text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.board.saved_name_label', 'Name')}
        </label>
        <input
          id={feldId}
          // biome-ignore lint/a11y/noAutofocus: Dialog mit genau einem Feld — der Fokus gehört dorthin.
          autoFocus
          value={name}
          maxLength={80}
          onChange={(e) => {
            setName(e.target.value);
            setFehler(null);
          }}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--line)', background: 'var(--surface)', color: 'var(--ink)' }}
          placeholder={t('v2.board.saved_name_placeholder', 'z. B. ProLo – Julia und ich')}
        />
        {fehler && (
          <p role="alert" className="text-xs" style={{ color: 'var(--danger)' }}>
            {fehlerText[fehler]}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onAbbrechen}
            className="text-sm px-3 py-1.5 rounded-md hover:bg-[var(--surface-2)]"
            style={{ color: 'var(--ink-2)', border: '1px solid var(--line-soft)' }}
          >
            {t('v2.board.saved_cancel', 'Abbrechen')}
          </button>
          <button
            type="submit"
            disabled={laeuft}
            className="text-sm px-3 py-1.5 rounded-md"
            style={{ background: 'var(--primary)', color: 'var(--on-primary)' }}
          >
            {t('v2.board.saved_confirm', 'Speichern')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
